/**
 * Единый набор иконок вместо эмодзи.
 * Штрих 1.5–1.6, currentColor, размер задаётся снаружи — так они
 * держат один вес и не разъезжаются между iOS, Android и десктопом.
 */
import type { SVGProps } from 'react';

type P = SVGProps<SVGSVGElement> & { size?: number };
const S = ({ size = 18, children, ...rest }: P & { children: React.ReactNode }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true" {...rest}>
    {children}
  </svg>
);

export const IconSearch = (p: P) => (
  <S {...p}>
    <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </S>
);

export const IconHeart = ({ filled, ...p }: P & { filled?: boolean }) => (
  <S {...p}>
    <path
      d="M10 17s-6.5-4-6.5-8.2A3.6 3.6 0 0110 6.5a3.6 3.6 0 016.5 2.3C16.5 13 10 17 10 17z"
      stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
      fill={filled ? 'currentColor' : 'none'}
    />
  </S>
);

export const IconCart = (p: P) => (
  <S {...p}>
    <path d="M2.5 3h2l1.8 8.4a1.5 1.5 0 001.47 1.18h6.9a1.5 1.5 0 001.46-1.15L17.5 6H5.2"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="8" cy="16.5" r="1.2" fill="currentColor" />
    <circle cx="14.5" cy="16.5" r="1.2" fill="currentColor" />
  </S>
);

export const IconCheck = (p: P) => (
  <S {...p}>
    <path d="M4 10.5l4 4 8-9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </S>
);

export const IconChevron = (p: P) => (
  <S {...p}>
    <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </S>
);

export const IconDot = (p: P) => (
  <S {...p}><circle cx="10" cy="10" r="3" fill="currentColor" /></S>
);

export const IconBell = (p: P) => (
  <S {...p}>
    <path d="M6 8a4 4 0 118 0c0 3 1.2 4.4 1.8 5H4.2C4.8 12.4 6 11 6 8z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8.4 15.5a1.7 1.7 0 003.2 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </S>
);

export const IconMessage = (p: P) => (
  <S {...p}>
    <path d="M3 5.5A2.5 2.5 0 015.5 3h9A2.5 2.5 0 0117 5.5v6A2.5 2.5 0 0114.5 14H8l-4 3.2V14h-.5A2.5 2.5 0 013 11.5v-6z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </S>
);

export const IconBox = (p: P) => (
  <S {...p} viewBox="0 0 24 24">
    <path d="M3 8l9-4 9 4-9 4-9-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M3 8v8l9 4 9-4V8" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M12 12v8" stroke="currentColor" strokeWidth="1.5" />
  </S>
);

export const IconInfo = (p: P) => (
  <S {...p}>
    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10 6.5v4.2M10 13.6v.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </S>
);

export const IconWarn = (p: P) => (
  <S {...p}>
    <path d="M10 3l7.5 13h-15L10 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M10 8v3.4M10 13.6v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </S>
);

/** Обрыв связи: волны сигнала с перечёркиванием. */
export const IconOffline = (p: P) => (
  <S {...p}>
    <path d="M2.6 7.6a11.5 11.5 0 0114.8 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M5.4 10.7a7.5 7.5 0 019.2 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8.1 13.7a3.5 3.5 0 013.8 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M10 16.5v.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M3.6 3.4l12.8 13.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </S>
);

export const IconTelegram = (p: P) => (
  <S {...p}>
    <path fill="currentColor" d="M17.6 3.4L2.9 9.1c-.9.4-.9.9-.2 1.1l3.7 1.2 1.4 4.3c.2.5.3.7.7.7.3 0 .5-.2.7-.4l1.8-1.7 3.7 2.7c.7.4 1.2.2 1.4-.6l2.5-11.8c.2-1-.4-1.4-1-1.2z" />
  </S>
);

export const IconSliders = (p: P) => (
  <S {...p}>
    <path d="M4 6h12M4 10h12M4 14h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="6" r="1.8" fill="currentColor" />
    <circle cx="13" cy="10" r="1.8" fill="currentColor" />
    <circle cx="7" cy="14" r="1.8" fill="currentColor" />
  </S>
);

/* ── Чертёжная графика вместо фотографий ─────────────────────────────────
   Пока у товара нет фото, вместо серого прямоугольника рисуется линейная
   схема категории: это выглядит как намерение, а не как поломка. */

const ART: Record<string, React.ReactNode> = {
  crosshair: <><path d="M6 30h8m36 0h8" /><path d="M14 24h36v12H14z" /><path d="M50 22h6v16h-6z" /><path d="M8 25h6v10H8z" /><path d="M28 18h8v6h-8z" /><circle cx="32" cy="30" r="5" /><path d="M32 25v10M27 30h10" /></>,
  radar: <><path d="M14 20h36a4 4 0 014 4v22a4 4 0 01-4 4H14a4 4 0 01-4-4V24a4 4 0 014-4z" /><path d="M17 27h30M17 33h22" /><path d="M20 42c4-5 9-5 13 0 4 5 9 5 13 0" /><circle cx="32" cy="14" r="3" /></>,
  fish: <><path d="M8 52L48 12" /><path d="M14 46l4 4M22 38l4 4M30 30l4 4M38 22l4 4" /><path d="M46 10h8v6h-8z" /><circle cx="18" cy="49" r="6" /><path d="M18 45v8" /></>,
  anchor: <><path d="M12 32c8-10 26-10 34 0-8 10-26 10-34 0z" /><circle cx="42" cy="30" r="2" /><path d="M12 32l-6-6v12l6-6z" /><path d="M22 40v6M22 46l-3 4M22 46l3 4M36 40v6M36 46l-3 4M36 46l3 4" /></>,
  sailboat: <><path d="M8 34h48l-7 14H15L8 34z" /><path d="M20 34V20h6l12 14" /><path d="M8 52h48" /></>,
  shirt: <><path d="M22 12l10 5 10-5 10 8-4 8-3-2v27H19V26l-3 2-4-8 10-8z" /><path d="M32 17v33" /></>,
  tent: <><path d="M32 12L10 48h44L32 12z" /><path d="M32 12v36" /><path d="M24 48l8-16 8 16" /></>,
  axe: <><path d="M20 12c10-3 20 0 24 8s-2 16-12 16l-8-4 4-8-8-12z" /><path d="M24 32L14 54" /><path d="M12 50l6 4" /></>,
  battery: <><rect x="8" y="18" width="48" height="30" rx="4" /><rect x="14" y="24" width="16" height="12" rx="2" /><circle cx="42" cy="28" r="3" /><circle cx="42" cy="38" r="3" /><path d="M18 14h12M34 14h12" /></>,
  flame: <><path d="M14 26a18 18 0 0136 0" /><rect x="24" y="26" width="16" height="14" rx="3" /><path d="M32 40v8M26 46l-4 6M38 46l4 6" /></>,
  binoculars: <><rect x="10" y="16" width="16" height="32" rx="6" /><rect x="38" y="16" width="16" height="32" rx="6" /><path d="M26 26h12M26 34h12" /></>,
};

export function ProductArt({ kind, opacity = 0.62 }: { kind?: string | null; opacity?: number }) {
  const art = (kind && ART[kind]) || ART.radar;
  return (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeOpacity={opacity}
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      style={{ color: 'var(--c-accent)' }} aria-hidden="true">
      {art}
    </svg>
  );
}

export function CategoryIcon({ name }: { name?: string | null }) {
  return <ProductArt kind={name} opacity={0.95} />;
}
